import { yupResolver } from '@hookform/resolvers/yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, CircularProgress, Collapse, Tooltip, Typography } from '@mui/material';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Alert } from '../../../../shared/lib/common/Alert';
import { BUTTON_TEXT_CANCEL, BUTTON_TEXT_CHANGE_PASSWORD, BUTTON_TEXT_UPDATE, LABEL_ACCOUNT_PASSWORD, NOTE_ACCOUNT_PASSWORD_SEPARATE_FROM_PIN } from '../../../../shared/lib/constants';
import { withTrademark } from '../../../../shared/lib/common/Tokenizer';
import { MSG_PASSWORD_UPDATED_SUCCESSFULLY } from '../../app/constants';
import { CustomController } from '../../../../shared/lib/common/CustomController';
import palette from '../../../../shared/lib/theme/palette';
import { useAppContext } from '../../app/contexts/AppContext';
import { ResetPasswordData } from '../../app/interfaceTypes';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { resetPasswordSchema } from '../../app/validationSchema';
import { useUpdateUserPasswordMutation } from '../../generated';
import { useMobileView } from '../../app/hooks/useMobileView';

export const ChangeUserPassword = () => {
  const { blackTwo, borderLight } = palette;
  const { isPasswordChanged, dispatch, currentUser } = useAppContext();
  const { id } = currentUser || {};
  const mediumViewport = useMobileView();

  const passwordEditOpen = () => dispatch({ type: ActionType.SET_IS_PASSWORD_CHANGED, isPasswordChanged: !isPasswordChanged });

  const passwordEditClose = () => {
    resetFields();
    dispatch({ type: ActionType.SET_IS_PASSWORD_CHANGED, isPasswordChanged: false });
  };

  const [updateUserPassword, { loading }] = useUpdateUserPasswordMutation({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { updateUserPassword } = data;

      if (updateUserPassword) {
        Alert.success(MSG_PASSWORD_UPDATED_SUCCESSFULLY);
      }
      passwordEditClose();
    }
  });

  const methods = useForm<ResetPasswordData, any, ResetPasswordData>({
    mode: 'all',
    resolver: yupResolver(resetPasswordSchema),

    defaultValues: {
      newPassword: '',
      repeatPassword: ''
    }
  });

  const { handleSubmit, reset } = methods;

  const onSubmit: SubmitHandler<ResetPasswordData> = async (data) => {
    const { newPassword } = data;

    id &&
      (await updateUserPassword({
        variables: {
          updateUserPasswordInput: {
            newPassword: newPassword,
            userId: id
          }
        }
      }));
  };

  const resetFields = () => {
    reset({
      newPassword: '',
      repeatPassword: ''
    });
  };

  return (
    <Box pt="16px">
      <FormProvider {...methods}>
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" alignItems="center" gap="6px" pb="8px">
            <Typography variant="h2" sx={{ textTransform: 'uppercase', color: blackTwo }}>
              {LABEL_ACCOUNT_PASSWORD}
            </Typography>
            <Tooltip title={<>{withTrademark(NOTE_ACCOUNT_PASSWORD_SEPARATE_FROM_PIN)}</>} arrow placement="top-start">
              <InfoOutlinedIcon sx={{ fontSize: '1rem', color: blackTwo, cursor: 'pointer', opacity: 0.7 }} />
            </Tooltip>
          </Box>

          <Collapse in={!isPasswordChanged}>
            <Button variant="text" sx={{ border: `1px solid ${borderLight}` }} onClick={passwordEditOpen}>
              {BUTTON_TEXT_CHANGE_PASSWORD}
            </Button>
          </Collapse>

          <Collapse in={isPasswordChanged}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexDirection={mediumViewport ? 'column' : 'row'}>
              <Box display="flex" gap={mediumViewport ? 0 : 1} width={mediumViewport ? "100%" : "60%"} flexDirection={mediumViewport ? 'column' : 'row'} >
                <Box maxWidth={mediumViewport ? "100%" : '50%'} width="100%">
                  <CustomController controllerName="newPassword" controllerLabel="New Password" fieldType="password" />
                </Box>
                <Box maxWidth={mediumViewport ? "100%" : '50%'} width="100%">
                  <CustomController controllerName="repeatPassword" controllerLabel="Confirm Password" fieldType="password" />
                </Box>
              </Box>

              <Box display="flex" gap={1} height="56px" mt="8px" width={mediumViewport ? "100%" : "auto"}>
                <Button fullWidth onClick={passwordEditClose} variant="text" sx={{ border: `1px solid ${borderLight}` }}>
                  {BUTTON_TEXT_CANCEL}
                </Button>

                <Button fullWidth type="submit" variant="contained" color="primary" disabled={loading} endIcon={loading && <CircularProgress size={20} />}>
                  {BUTTON_TEXT_UPDATE}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </form>
      </FormProvider>
    </Box>
  );
};
